import React, { FunctionComponent, useCallback, useMemo } from 'react';

import { useReport } from 'reports/pages/AddNewReport/AddNewReport';
import { extractTitleOrSubTileOrReportNameOrDesc } from 'reports/utils/utils';
import { Grid } from '@material-ui/core';
import {
  BigCommentIcon,
  BigHeartIcon,
  CalenderIcon,
  PostImage,
  Resume,
  RetweetTweetIcon,
  TopicsIcon,
  TotalInf,
} from 'app/assets/images/icons';
import { AddComment } from '@material-ui/icons';
import IconCard from 'app/components/IconCard';
import { _t } from 'app/utils/i18n';
import { checkTranslated } from 'app/utils/helpers';
import { apiRoutes } from 'posts/utils/apiRoutes';
import DonutChart from 'app/components/Charts/DonutChart';
import SecondaryTable from 'app/components/Tables/SecondaryTable';
import { nFormatter } from 'app/utils/numbersFormater';
import { path } from 'app/utils/path';
import { Link } from 'react-router-dom';
import InfluencerInformationRow from 'app/components/InfluencerInformationRow';
import { storeInfluencerAsLinkedin } from 'influencers/Requests';
import RecentMedia from 'influencers/components/RecentMedia';
import PostRow from 'app/components/PostRow';

interface Props {
  data: any;
  id: number;
  isReport?: boolean;
  isTemplate?: boolean;
  tabName: string;
  items?: any[];
  setActiveTab: (n: number) => void;
  isPreview?: boolean;
}

const LinkedinPostAnalyzed: FunctionComponent<Props> = ({
  data,
  id,
  tabName,
  items,
  isTemplate,
  isReport,
  setActiveTab,
  isPreview,
}) => {
  const report = useReport() as any;
  type TRenderCharts = { id: string; component: JSX.Element }[] | false;
  const checkIsReport = useCallback(
    (report: any, notReport: any = undefined) =>
      isReport && !isTemplate ? report : notReport,
    [isReport, isTemplate],
  );
  const linkedinCustomFilters = {
    post_id: Number(id),
    is_sample: isPreview,
    limit: 5,
  };
  const linkedinFrequentUsedHashtags = (isWord: boolean) => [
    {
      title: isWord ? _t('basic:word') : _t('basic:hashtags'),
      key: isWord ? 'word_name' : 'hashtag_name',
      render: (val: any, row: any) => {
        return (
          <div>
            {isWord ? (
              <div>
                {checkTranslated(
                  row?.word_name?.original,
                  row?.word_name?.arabic,
                )}
              </div>
            ) : (
              <Link to={`${path.hashtags.HASHTAG_VIEW_URI}/${row?.hashtag_id}`}>
                {checkTranslated(
                  row?.hashtag_name?.original,
                  row?.hashtag_name?.arabic,
                )}
              </Link>
            )}
          </div>
        );
      },
    },
    {
      title: _t('trends:mentions'),
      key: isWord ? 'word_count' : 'hashtag_count',
      render: (value: number) => <p>{nFormatter(value)}</p>,
    },
  ];
  const renderTiktokCharts: TRenderCharts = [
    {
      id: 'posts_linkedin_post_publish_date',
      component: (
        <IconCard
          title={
            _t('posts:post_date') +
            ` ${isReport ? `( ${_t('posts:linkedin')} )` : ''}`
          }
          cardData={{
            icon: <CalenderIcon />,
            count: data?.published_at,
          }}
          compId={'posts_linkedin_post_publish_date'}
          grid={{
            lg: 3,
            sm: 12,
          }}
          hideIfNoData={!isReport}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_linkedin_post_publish_date'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    {
      id: 'posts_linkedin_post_type',
      component: (
        <IconCard
          title={
            _t('posts:post_type') +
            ` ${isReport ? `( ${_t('posts:linkedin')} )` : ''}`
          }
          cardData={{
            icon:
              data?.post_type === 'VIDEO' ? (
                <Resume />
              ) : data?.post_type === 'IMAGE' ? (
                <PostImage />
              ) : data?.post_type === 'DOCUMENT' ? (
                <TopicsIcon />
              ) : data?.post_type === 'ARTICLE' ? (
                <TotalInf />
              ) : (
                <TopicsIcon />
              ),
            count: data?.post_type,
          }}
          compId={'posts_linkedin_post_type'}
          grid={{
            lg: 3,
            sm: 12,
          }}
          hideIfNoData={!isReport}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_linkedin_post_type'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    {
      id: 'posts_linkedin_post_total_reactions',
      component: (
        <IconCard
          title={
            _t('posts:post_total_reactions') +
            ` ${isReport ? `( ${_t('posts:total_reactions')} )` : ''}`
          }
          cardData={{
            icon: <BigHeartIcon />,
            count: data.total_reactions,
          }}
          compId={'posts_linkedin_post_total_reactions'}
          grid={{
            lg: 3,
            sm: 12,
          }}
          hideIfNoData={!isReport}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_linkedin_post_total_reactions'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    {
      id: 'posts_linkedin_post_comments',
      component: (
        <IconCard
          title={
            _t('posts:post_comments') +
            ` ${isReport ? `( ${_t('posts:tiktok')} )` : ''}`
          }
          cardData={{
            icon: <BigCommentIcon />,
            count: data.comments_count,
          }}
          compId={'posts_linkedin_post_comments'}
          grid={{
            lg: 3,
            sm: 12,
          }}
          hideIfNoData={!isReport}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_linkedin_post_comments'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    {
      id: 'posts_linkedin_post_reposts',
      component: (
        <IconCard
          hideIfNoData
          additionalComponents={checkIsReport(
            <AddComment {...report} compId={'post_reposts'} id={id} />,
          )}
          isReport={isReport}
          title={_t('posts:post_reposts')}
          cardData={{
            icon: <RetweetTweetIcon />,
            count: data?.repost_count,
          }}
          compId={'posts_linkedin_post_reposts'}
          grid={{
            lg: checkIsReport(3, 3),
            sm: 12,
          }}
        />
      ),
    },
    {
      id: 'posts_linkedin_post_comments_sentiment',
      component: (
        <DonutChart
          donutTitle={
            _t('posts:comments_sentiment') +
            ` ${isReport ? `( ${_t('posts:linkedin')} )` : ''}`
          }
          limit={5}
          showFilter
          hideInput
          title={_t('posts:comments')}
          compId={'posts_linkedin_post_comments_sentiment'}
          slug={apiRoutes.linkedinCommentSentimentAnalysis}
          hideIfNoData={!isReport}
          grid={{
            lg: 3,
            sm: 12,
          }}
          customFilters={linkedinCustomFilters}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_tiktolinkedin_comments_sentiment'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    {
      id: 'posts_linkedin_post_frequently_used_hashtags',
      component: (
        <SecondaryTable
          grid={{ item: true, lg: 3, sm: 12 }}
          tableCardTitle={_t('posts:frequently_used_hashtags')}
          hideIfNoData
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_linkedin_post_frequently_used_hashtags'}
              id={id}
            />,
          )}
          isReport={isReport}
          compId={'posts_linkedin_post_frequently_used_hashtags'}
          columns={linkedinFrequentUsedHashtags(false)}
          moduleName={apiRoutes.linkedinFrequentUsedHashtags}
          expandAction={() => {
            window.scrollTo(0, 0);
            setActiveTab(2);
          }}
          hideFilters
          customFilters={linkedinCustomFilters}
          notPlatforms
          distributed
          hideToggleView
        />
      ),
    },
    {
      id: 'posts_linkedin_post_media',
      component: (
        <RecentMedia
          mediaData={data?.attachments?.filter(
            (i: { type: string }) => i.type === 'VIDEO' || i.type === 'IMAGE',
          )}
          compId={'posts_linkedin_post_media'}
          title={
            _t('posts:post_media') +
            ` ${isReport ? `( ${_t('posts:linkedin')} )` : ''}`
          }
          hideIfNoData={!isReport}
          grid={{
            lg: 3,
            sm: 12,
          }}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_linkedin_post_media'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    {
      id: 'posts_linkedin_post_frequent_used_words',
      component: (
        <SecondaryTable
          grid={{ item: true, lg: 3, sm: 12 }}
          tableCardTitle={_t('posts:frequently_used_words')}
          hideIfNoData
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_linkedin_post_frequent_used_words'}
              id={id}
            />,
          )}
          isReport={isReport}
          compId={'posts_linkedin_post_frequent_used_words'}
          columns={linkedinFrequentUsedHashtags(true)}
          moduleName={apiRoutes.linkedinFrequentUsedWords}
          hideFilters
          customFilters={linkedinCustomFilters}
          notPlatforms
          distributed
          hideToggleView
        />
      ),
    },
    {
      id: 'posts_linkedin_post_influencer',
      component: (
        <InfluencerInformationRow
          compId="posts_linkedin_post_influencer"
          data={data}
          title={_t('posts:influencer_details')}
          storeInfluencer={storeInfluencerAsLinkedin}
          openInSource={_t('influencers:open_in_linkedin')}
          hideJoinedin
          customKey="linkedin_followers_count"
          grid={{
            sm: 12,
          }}
        />
      ),
    },
    {
      id: 'posts_linkedin_post_details',
      component: (
        <PostRow
          compId="posts_linkedin_post_details"
          data={data}
          title={_t('posts:linkedin_posts')}
          tabName="linkedin"
          isReport={!!items}
          grid={{
            sm: 12,
          }}
        />
      ),
    },
  ];
  const renderChartsList = (chartList: any) =>
    chartList.map((item: any, index: number) =>
      item?.component
        ? React.cloneElement(item.component, { key: item?.id || index })
        : null,
    );
  const renderPage = () => {
    if (tabName == 'linkedin') return renderChartsList(renderTiktokCharts);
    if (items) {
      const paragraph = items?.map((item) =>
        extractTitleOrSubTileOrReportNameOrDesc(item),
      );
      const chartsFilter = renderTiktokCharts?.map((item: any) =>
        items?.some((i: any) => i?.slug === item?.id)
          ? {
              ...item,
              order: items?.find((comp) => comp.slug === item.id)?.order,
            }
          : null,
      );

      return renderChartsList(
        [...chartsFilter, ...paragraph]
          .filter(Boolean)
          .sort((a, b) => a.order - b.order),
      );
    }
  };

  const memoizedRenderPage = useMemo(
    () => renderPage(),

    [tabName, report, data, id],
  );
  return (
    <Grid container spacing={4}>
      {memoizedRenderPage}
    </Grid>
  );
};

export default LinkedinPostAnalyzed;
